import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes";
import { apiFetchJson } from "../../utils/http";

const DEBOUNCE_MS = 300;

export default function SelectDepartamento({
  value,            // { id, nombre } o null
  onChange,         // (departamento | null) => void
  disabled = false,
  placeholder = "Buscar departamento...",
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sincroniza el value externo con el campo de texto
  useEffect(() => {
    if (value?.nombre) {
      setInputText(value.nombre);
    } else {
      setInputText("");
    }
  }, [value]);

  // Cierra el dropdown al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Buscar departamentos con debounce
  const fetchDepartamentos = async (query) => {
    setLoading(true);
    try {
      const url = apiRoutes.departamentos.autocomplete(query.trim(), 0, 20);
      const res = await apiFetchJson(url);
      const data = res?.items || [];
      setItems(data.map((it) => ({ id: it.id, nombre: it.nombre })));
      // Si hay resultados, abre el dropdown; si no, lo cierra
      if (data.length > 0) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    } catch {
      setItems([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);

    if (value && text.trim() === "") {
      onChange(null);   // limpia la selección si se borra el texto
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchDepartamentos(text);
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    // Al hacer foco, dispara una búsqueda con el texto actual (si hay) o con "" para mostrar todos
    fetchDepartamentos(inputText);
  };

  const handleSelect = (dep) => {
    onChange(dep);
    setInputText(dep.nombre);
    setOpen(false);
  };

  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        className="input"
        disabled={disabled}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2 text-gray-400"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm">
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">Buscando...</div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">Sin resultados.</div>
          )}

          {items.map((dep) => (
            <button
              key={dep.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
              onClick={() => handleSelect(dep)}
            >
              <div className="text-sm text-gray-700">{dep.nombre}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}